import React from 'react';
import LandingHeader from '../../ui/LandingHeader';
import LandingFooter from '../../ui/LandingFooter';
import CallToAction from '../CallToAction';

const ForEngineersPage: React.FC = () => {
    return (
        <div className="min-h-screen bg-background antialiased">
            <div className="relative z-10">
                <LandingHeader />
            </div>
            <main className="relative">
                {/* Hero Section */}
                <section className="pt-20 md:pt-32 pb-16 md:pb-24 px-4 relative overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute inset-0 -z-10">
                        <div className="absolute top-0 left-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                        <div className="absolute bottom-0 right-1/4 w-48 md:w-72 h-48 md:h-72 bg-primary/5 rounded-full blur-3xl" />
                    </div>
                    
                    <div className="container mx-auto max-w-5xl">
                        <div className="text-center space-y-4 md:space-y-6">
                            <div className="inline-flex items-center px-3 md:px-4 py-1.5 md:py-2 rounded-full bg-primary/10 text-primary text-xs md:text-sm font-medium mb-2 md:mb-4">
                                <svg className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                </svg>
                                For Engineers
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-foreground">
                                Build Interactive Tools<br className="hidden md:block" />
                                <span className="text-primary">Without the Backend</span>
                            </h1>
                            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                                Create rich, interactive applications that run entirely in the browser. 
                                No servers, no APIs, no infrastructure. Just Python code that works like a document.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Features Grid */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-2 sm:px-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                            <div className="space-y-6 md:space-y-8">
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2 md:mb-3">Python-First Development</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed md:leading-relaxed">
                                            Write your entire application in Python, from UI to logic. Perfect for data scientists 
                                            and engineers who want to build interactive tools without learning frontend frameworks.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3 md:space-x-4">
                                    <div className="bg-primary/10 p-2 md:p-3 rounded-lg text-primary shrink-0">
                                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg md:text-xl font-semibold mb-2">Fully Static, Fully Interactive</h3>
                                        <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                            Build applications that deploy as static files but maintain full interactivity. 
                                            Perfect for internal tools, dashboards, and data visualizations that need to be shared easily.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-4 md:p-6 relative mt-8 md:mt-0">
                                <div className="absolute -top-3 -right-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Live Example
                                </div>
                                <pre className="text-xs md:text-sm text-muted-foreground font-mono overflow-x-auto">
                                    <code>{`# A complete interactive app in one file
import preswald as pw
import pandas as pd

# Load and process data
df = pd.read_csv('sales.csv')
summary = df.groupby('region').sum()

# Create interactive UI
app = pw.App("Sales Dashboard")

@app.page("Overview")
def overview():
    return pw.Column([
        pw.Chart(summary, type="bar"),
        pw.Table(df.head()),
        pw.Input("Filter by region", 
                on_change=lambda x: filter_data(x))
    ])

# Export as static file
app.export("sales_dashboard.html")`}</code>
                                </pre>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Use Cases */}
                <section className="py-16 md:py-24">
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Perfect For</h2>
                        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Internal Tools
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Internal Tools</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Build data dashboards, workflow tools, and internal utilities that can be shared via email or chat. 
                                    No deployment pipeline needed. Just export and share.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Data Viz
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Data Visualization</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Create interactive charts and visualizations that work offline. Perfect for data scientists 
                                    who need to share insights without setting up a server.
                                </p>
                            </div>

                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 hover:shadow-md transition-shadow relative group sm:col-span-2 md:col-span-1">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Prototyping
                                </div>
                                <div className="absolute top-4 right-4 text-muted-foreground/20 group-hover:text-primary/20 transition-colors">
                                    <svg className="w-8 h-8 md:w-12 md:h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                                    </svg>
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-3 md:mb-4">Prototyping</h3>
                                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                                    Quickly prototype ideas with real data without worrying about infrastructure. 
                                    Test concepts with stakeholders before committing to full development.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Benefits */}
                <section className="py-16 md:py-24 bg-muted/50 relative">
                    <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                    <div className="container mx-auto max-w-5xl px-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-center mb-12 md:mb-16">Why Engineers Choose Preswald</h2>
                        <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Development
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Development Experience</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Write everything in Python. No frontend framework needed.</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Use familiar data science libraries (pandas, numpy)</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Instant preview and iteration</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Works with your existing Python tooling</span>
                                    </li>
                                </ul>
                            </div>
                            <div className="bg-card rounded-lg border shadow-sm p-6 md:p-8 relative">
                                <div className="absolute -top-3 -left-3 bg-primary/10 text-primary px-2 md:px-3 py-0.5 md:py-1 rounded-full text-xs md:text-sm font-medium">
                                    Deployment
                                </div>
                                <h3 className="text-lg md:text-xl font-semibold mb-4 md:mb-6">Deployment & Sharing</h3>
                                <ul className="space-y-3 md:space-y-4 text-muted-foreground">
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Export as single HTML file</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Share via email, chat, or wiki</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">Works offline and on any device</span>
                                    </li>
                                    <li className="flex items-center space-x-3">
                                        <span className="text-primary">•</span>
                                        <span className="text-sm md:text-base">No servers, APIs, or infrastructure needed</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                <CallToAction />
            </main>
            <LandingFooter />
        </div>
    );
};

export default ForEngineersPage; 